/**
 * AuditTrailModal Component
 * Paginated modal for viewing individual LLM call records
 */

import { useState, useEffect } from 'react';
import {
  X,
  Download,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Filter,
  Calendar,
  RefreshCw,
} from 'lucide-react';
import { useLLMAuditTrail } from '../../hooks/useLLMCosts';

// Format timestamp for display
function formatTimestamp(ts) {
  if (!ts) return '-';
  try {
    return new Date(ts).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return ts;
  }
}

// Format cost for display
function formatCost(cost) {
  if (cost === null || cost === undefined) return '-';
  return `$${cost.toFixed(6)}`;
}

// Format latency for display
function formatLatency(ms) {
  if (ms === null || ms === undefined) return '-';
  return `${Math.round(ms)}ms`;
}

// Get date N days ago in YYYY-MM-DD format
function getDateNDaysAgo(days) {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().split('T')[0];
}

// Get today's date in YYYY-MM-DD format
function getToday() {
  return new Date().toISOString().split('T')[0];
}

export default function AuditTrailModal({ tenantId, isOpen, onClose }) {
  const [startDate, setStartDate] = useState(getDateNDaysAgo(7));
  const [endDate, setEndDate] = useState(getToday());
  const [modelFilter, setModelFilter] = useState('');
  const [page, setPage] = useState(0);
  const pageSize = 50;

  // Fetch audit trail data
  const { data, isLoading, refetch } = useLLMAuditTrail(
    tenantId,
    startDate,
    endDate,
    pageSize,
    page * pageSize
  );

  const calls = data?.calls || [];
  const totalCount = data?.total_count || 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  // Extract unique models for filter dropdown
  const uniqueModels = [...new Set(calls.map((c) => c.model).filter(Boolean))];

  // Filter calls by model if filter is set
  const filteredCalls = modelFilter
    ? calls.filter((c) => c.model === modelFilter)
    : calls;

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [startDate, endDate, tenantId]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Export to CSV
  const handleExport = () => {
    if (!calls.length) return;

    const headers = [
      'Timestamp',
      'Model',
      'Provider',
      'Operation',
      'Route ID',
      'Prompt Tokens',
      'Completion Tokens',
      'Total Tokens',
      'Cost (USD)',
      'Latency (ms)',
    ];

    const rows = calls.map((c) => [
      c.timestamp,
      c.model || '',
      c.provider || '',
      c.operation_type || '',
      c.route_id || '',
      c.prompt_tokens || 0,
      c.completion_tokens || 0,
      c.total_tokens || 0,
      c.cost_usd || 0,
      c.latency_ms || 0,
    ]);

    const csvContent = [headers, ...rows].map((row) => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `llm-audit-${tenantId}-${startDate}-to-${endDate}.csv`;
    link.click();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 z-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-4 md:inset-10 bg-white dark:bg-gray-900 rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              LLM Call Audit Trail
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {totalCount.toLocaleString()} total calls for {tenantId}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          {/* Date Range */}
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-2 py-1 text-sm border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
            <span className="text-gray-400">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-2 py-1 text-sm border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
          </div>

          {/* Model Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={modelFilter}
              onChange={(e) => setModelFilter(e.target.value)}
              className="px-2 py-1 text-sm border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              <option value="">All Models</option>
              {uniqueModels.map((model) => (
                <option key={model} value={model}>
                  {model}
                </option>
              ))}
            </select>
          </div>

          {/* Refresh */}
          <button
            onClick={() => refetch()}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
          >
            <RefreshCw className="w-4 h-4" />
          </button>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Export */}
          <button
            onClick={handleExport}
            disabled={!calls.length}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : filteredCalls.length === 0 ? (
            <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
              No LLM calls found for the selected period
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                <tr>
                  <th className="text-left px-4 py-3 text-gray-500 dark:text-gray-400 font-medium">
                    Timestamp
                  </th>
                  <th className="text-left px-4 py-3 text-gray-500 dark:text-gray-400 font-medium">
                    Model
                  </th>
                  <th className="text-left px-4 py-3 text-gray-500 dark:text-gray-400 font-medium">
                    Operation
                  </th>
                  <th className="text-left px-4 py-3 text-gray-500 dark:text-gray-400 font-medium">
                    Route
                  </th>
                  <th className="text-right px-4 py-3 text-gray-500 dark:text-gray-400 font-medium">
                    In/Out
                  </th>
                  <th className="text-right px-4 py-3 text-gray-500 dark:text-gray-400 font-medium">
                    Cost
                  </th>
                  <th className="text-right px-4 py-3 text-gray-500 dark:text-gray-400 font-medium">
                    Latency
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {filteredCalls.map((call, idx) => (
                  <tr
                    key={`${call.timestamp}-${idx}`}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800/50"
                  >
                    <td className="px-4 py-2 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                      {formatTimestamp(call.timestamp)}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-gray-900 dark:text-white">
                      {call.model || '-'}
                    </td>
                    <td className="px-4 py-2 text-gray-600 dark:text-gray-300">
                      {call.operation_type || '-'}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-gray-600 dark:text-gray-400">
                      {call.route_id || '-'}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-300 whitespace-nowrap">
                      <span className="text-green-600">{call.prompt_tokens || 0}</span>
                      <span className="text-gray-400 mx-1">/</span>
                      <span className="text-blue-600">{call.completion_tokens || 0}</span>
                    </td>
                    <td className="px-4 py-2 text-right font-medium text-gray-900 dark:text-white">
                      {formatCost(call.cost_usd)}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-300">
                      {formatLatency(call.latency_ms)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Showing {page * pageSize + 1} - {Math.min((page + 1) * pageSize, totalCount)} of{' '}
            {totalCount.toLocaleString()}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </button>
            <span className="text-sm text-gray-600 dark:text-gray-300">
              Page {page + 1} of {totalPages || 1}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
